import { useMemo } from 'react';
import { ReactComponent as AiGenerateIcon } from '@elementary/common-ui/assets/24x24/ai-generate-no-color.svg';
import { ReactComponent as CriticalAssetOff } from '@elementary/common-ui/assets/16x16/critical-asset-tag-unmark.svg';
import { ReactComponent as CriticalAssetOn } from '@elementary/common-ui/assets/16x16/critical-asset-tag.svg';
import { ReactComponent as DocumentIcon } from '@elementary/common-ui/assets/20x20/document-2.svg';
import { ReactComponent as OwnerIcon } from '@elementary/common-ui/assets/20x20/owners.svg';
import { ReactComponent as TagIcon } from '@elementary/common-ui/assets/20x20/tags.svg';
import { ReactComponent as AddTestIcon } from '@elementary/common-ui/assets/add-test.svg';
import { ReactComponent as CriticalAssetIcon } from '@elementary/common-ui/assets/critical-asset.svg';
import {
  ActionsMenuButton,
  Flex,
  FloatingActionsMenu,
  Menu,
  SvgIcon,
} from '@elementary/common-ui/components';
import useAccount from '@elementary/common-ui/hooks/useAccount';
import useAiChatPanel from '@elementary/common-ui/hooks/useAiChatPanel';
import useCriticalAssets from '@elementary/common-ui/hooks/useCriticalAssets';
import useEnvPermission from '@elementary/common-ui/hooks/useEnvPermission';
import useHasDbtProjects from '@elementary/common-ui/hooks/useHasDbtProjects';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import { CircularProgress } from '@mui/material';
import {
  ADD_TEST_RESOURCES_TABLE_SELECT_ALL,
  ADD_TEST_RESOURCES_TABLE_FILTERS,
} from '@elementary/common-ui/constants/urlParams';
import useNavigateWithEnv from 'shared/hooks/useNavigateWithEnv';
import { useCodeRepositoryIntegration } from 'shared/hooks/integrations';

type BulkEditProperty = 'tags' | 'owners' | 'description';

const ActionSpinner = () => (
  <Flex sx={{ width: '20px' }}>
    <CircularProgress sx={{ color: 'content.primary' }} size={16} />
  </Flex>
);

type Props = {
  selectedAssets?: string[];
  onOpenWizard?: (val: BulkEditProperty) => void;
  isCriticalAssetsLoading?: boolean;
  isEditable?: boolean;
  onClose: () => void;
  type?: 'asset' | 'column';
  options?: ('test' | 'criticality' | 'tags' | 'owners' | 'description')[];
  showWithoutSelection?: boolean;
};

const AssetsTableActionMenu = ({
  selectedAssets = [],
  onOpenWizard,
  isCriticalAssetsLoading,
  isEditable,
  onClose,
  type = 'asset',
  options = ['test', 'criticality', 'tags', 'owners', 'description'],
  showWithoutSelection,
}: Props) => {
  const selectedEnvId = useSelectedEnv();
  const { isRepositoryConnected } = useCodeRepositoryIntegration({
    envId: selectedEnvId,
  });
  const { setIsCriticalAssets } = useCriticalAssets({});
  const navigate = useNavigateWithEnv();
  const hasPermission = useEnvPermission();
  const hasDbtProjects = useHasDbtProjects();
  const { data: account } = useAccount();
  const { openPanel } = useAiChatPanel();

  const isAsset = type === 'asset';
  const isCriticalLoading =
    setIsCriticalAssets.isLoading || isCriticalAssetsLoading;

  const disableAddTestTooltip = !isEditable
    ? 'Tests are supported only for models and sources'
    : !hasPermission('EDIT_TEST_CONFIGURATIONS')
    ? 'You do not have permission to add tests'
    : undefined;

  const disabledActionTooltip = !hasDbtProjects
    ? 'Editing tags, owners and descriptions requires a dbt project in this environment'
    : !isRepositoryConnected
    ? 'Connect your code repository to edit assets'
    : !isEditable
    ? 'Some of the selected assets are not editable'
    : undefined;

  const disableDescriptionsTooltip =
    disabledActionTooltip ||
    (selectedAssets.length > 200
      ? 'Too many assets were selected (max 200)'
      : undefined);

  const actionsMenuOptions = useMemo(() => {
    const menuOptions = [];

    const navigateToAddTest = () => {
      const mode = isAsset ? 'tables' : 'columns';
      const params = new URLSearchParams();

      params.set(ADD_TEST_RESOURCES_TABLE_SELECT_ALL, 'true');
      params.set(
        ADD_TEST_RESOURCES_TABLE_FILTERS,
        JSON.stringify([
          {
            property: 'id',
            operator: 'equals',
            value: selectedAssets,
          },
        ]),
      );

      navigate(`/test-management/add-test/${mode}?${params.toString()}`);
    };

    if (options.includes('test')) {
      menuOptions.push({
        value: 'test',
        itemRenderer: () =>
          account?.ai_features_enabled ? (
            <Menu
              menuButton={
                <ActionsMenuButton
                  tooltip={disableAddTestTooltip}
                  disabled={!!disableAddTestTooltip}
                  label="Add tests"
                  icon={
                    <SvgIcon fillColor="icon.primary">
                      <AddTestIcon />
                    </SvgIcon>
                  }
                />
              }
              options={[
                {
                  value: 'ai',
                  label: 'Add tests with AI',
                  icon: (
                    <SvgIcon width={16} fillColor="icon.primary">
                      <AiGenerateIcon />
                    </SvgIcon>
                  ),
                },
                {
                  value: 'manual',
                  label: 'Add tests manually',
                  icon: (
                    <SvgIcon width={16} fillColor="icon.primary">
                      <AddTestIcon />
                    </SvgIcon>
                  ),
                },
              ]}
              onChange={(value) => {
                if (value === 'ai') {
                  openPanel({
                    starterKey: isAsset ? 'addTests' : 'addColumnTests',
                    origin: 'ai_cta',
                  });
                } else if (value === 'manual') {
                  navigateToAddTest();
                }
              }}
              closeOnChange
              anchorOrigin={{ vertical: -110, horizontal: 10 }}
            />
          ) : (
            <ActionsMenuButton
              tooltip={disableAddTestTooltip}
              disabled={!!disableAddTestTooltip}
              label="Add tests"
              icon={
                <SvgIcon fillColor="icon.primary">
                  <AddTestIcon />
                </SvgIcon>
              }
              onClick={navigateToAddTest}
            />
          ),
      });
    }

    if (options.includes('criticality')) {
      menuOptions.push({
        value: 'criticality',
        icon: <CriticalAssetIcon width={20} />,
        itemRenderer: () => (
          <Menu
            menuButton={
              <ActionsMenuButton
                label="Critical"
                icon={
                  isCriticalLoading ? (
                    <ActionSpinner />
                  ) : (
                    <CriticalAssetIcon width={20} />
                  )
                }
              />
            }
            options={[
              {
                value: 'true',
                label: 'Critical',
                icon: <CriticalAssetOn />,
              },
              {
                value: 'false',
                label: 'Not critical',
                icon: <CriticalAssetOff />,
              },
            ]}
            onChange={(isCritical) =>
              setIsCriticalAssets.setIsCriticalAssetsMutation({
                assetsIds: selectedAssets,
                isCriticalValue: isCritical === 'true',
              })
            }
            closeOnChange
            anchorOrigin={{ vertical: -110, horizontal: 10 }}
          />
        ),
      });
    }

    if (options.includes('tags')) {
      menuOptions.push({
        label: 'Tags',
        value: 'tags',
        icon: <TagIcon />,
        disabled: !!disabledActionTooltip,
        tooltip: disabledActionTooltip,
      });
    }

    if (options.includes('owners')) {
      menuOptions.push({
        label: 'Owners',
        value: 'owners',
        icon: <OwnerIcon />,
        disabled: !!disabledActionTooltip,
        tooltip: disabledActionTooltip,
      });
    }

    if (options.includes('description')) {
      menuOptions.push({
        label: 'Descriptions',
        value: 'description',
        icon: <DocumentIcon />,
        disabled: !!disableDescriptionsTooltip,
        tooltip: disableDescriptionsTooltip,
      });
    }
    return menuOptions;
  }, [
    account?.ai_features_enabled,
    isAsset,
    disableAddTestTooltip,
    disableDescriptionsTooltip,
    disabledActionTooltip,
    isCriticalLoading,
    options,
    navigate,
    openPanel,
    selectedAssets,
    setIsCriticalAssets,
  ]);

  return (
    <FloatingActionsMenu
      itemCount={selectedAssets.length}
      countLabel={isAsset ? 'Asset' : 'Column'}
      countLabelPlural={isAsset ? 'Assets' : 'Columns'}
      actions={actionsMenuOptions}
      onChange={(val) => onOpenWizard?.(val as BulkEditProperty)}
      onClose={onClose}
      showWithoutSelection={showWithoutSelection}
    />
  );
};

export default AssetsTableActionMenu;
